import { ChildType, EntryType } from "@common/types/tree-types";
import {
  TreeProps,
  updateEditStructurePageInitialData,
  updateEditStructurePageInitialDataFiltered,
  updateEditStructurePageTransferData,
} from "@src/reducers/your-reducer";
import store from "@src/reducers/store";

/**
 * Retrieves all the unique IDs from a tree-like structure starting from the given node.
 * @param {ChildType} machineStructureDataNode - The root node of the tree-like structure.
 * @returns {Set<string>} - A Set containing all the unique IDs found in the tree structure.
 */
const getAllIds = (machineStructureDataNode: ChildType): Set<string> => {
  const idsSet: Set<string> = new Set();

  // Function to recursively traverse the tree and add IDs to the Set
  const traverse = (node: ChildType | EntryType) => {
    if (node.id) {
      idsSet.add(node.id); // Add the ID to the Set
    }

    if (node.children) {
      node.children.forEach(traverse); // Recursively traverse child nodes
    }
  };

  traverse(machineStructureDataNode); // Start traversal from the root node

  return idsSet;
};

const markAllNodesAsTransferred = (
  initialStructureDataNode: ChildType,
  ids: Set<string>,
): ChildType => {
  const copiedNode = { ...initialStructureDataNode };

  copiedNode.transferred = ids.has(copiedNode.id);

  if (copiedNode.children) {
    copiedNode.children = copiedNode.children.map((child) =>
      markAllNodesAsTransferred(child, ids),
    );
  }

  return copiedNode;
};

const getEditStructurePageInitialData = () => {
  return store.getState().yourSlice.editStructurePageInitialData;
};

const getEditStructurePageInitialDataFiltered = () => {
  return store.getState().yourSlice.editStructurePageInitialDataFiltered;
};

export const updateEditStructurePageInitialDataTransferredState = (ids) => {
  const editStructurePageInitialData = getEditStructurePageInitialData();

  if (editStructurePageInitialData) {
    const newEditStructurePageInitialRootNode = markAllNodesAsTransferred(
      editStructurePageInitialData.nodeset.root_node,
      ids,
    );

    const newEditStructurePageInitialData = {
      ...editStructurePageInitialData,
      nodeset: {
        ...editStructurePageInitialData.nodeset,
        root_node: newEditStructurePageInitialRootNode,
      },
    };

    store.dispatch(
      updateEditStructurePageInitialData(newEditStructurePageInitialData),
    );
  }
};

const updateEditStructurePageInitialDataFilteredTransferredState = (ids) => {
  const editStructurePageInitialDataFiltered =
    getEditStructurePageInitialDataFiltered();

  if (editStructurePageInitialDataFiltered) {
    const newEditStructurePageInitialFilteredRootNode =
      markAllNodesAsTransferred(
        editStructurePageInitialDataFiltered.nodeset.root_node,
        ids,
      );

    const newEditStructurePageInitialDataFiltered = {
      ...editStructurePageInitialDataFiltered,
      nodeset: {
        ...editStructurePageInitialDataFiltered.nodeset,
        root_node: newEditStructurePageInitialFilteredRootNode,
      },
    };

    store.dispatch(
      updateEditStructurePageInitialDataFiltered(
        newEditStructurePageInitialDataFiltered,
      ),
    );
  }
};

const updateEditStructurePageTransferDataState = (
  editStructurePageTransferData,
) => {
  store.dispatch(
    updateEditStructurePageTransferData(editStructurePageTransferData),
  );
};

const updateTransferredWithEmptyMachineStructure = (
  editStructurePageTransferData: TreeProps,
): void => {
  const ids: Set<string> = new Set();

  updateEditStructurePageInitialDataTransferredState(ids);
  updateEditStructurePageInitialDataFilteredTransferredState(ids);
  updateEditStructurePageTransferDataState(editStructurePageTransferData);
};

const updateTransferredWithPopulatedMachineStructure = (
  editStructurePageTransferData: TreeProps,
): void => {
  const ids = getAllIds(editStructurePageTransferData.nodeset.root_node);

  updateEditStructurePageInitialDataTransferredState(ids);
  updateEditStructurePageInitialDataFilteredTransferredState(ids);
  updateEditStructurePageTransferDataState(editStructurePageTransferData);
};

export const saveEditStructurePageTransferData = (
  editStructurePageTransferData: TreeProps,
): void => {
  if (
    editStructurePageTransferData === undefined ||
    editStructurePageTransferData.nodeset === null
  ) {
    updateTransferredWithEmptyMachineStructure(editStructurePageTransferData);
  } else {
    updateTransferredWithPopulatedMachineStructure(
      editStructurePageTransferData,
    );
  }
};
